import { z } from 'zod';
import { mitigationDryingDateSchema, mitigationDryingIdSchema, mitigationDryingReasonSchema, } from './mitigation-drying-workspace.contract.js';
export const mitigationDailyMonitoringCapability = 'mitigation.daily-monitoring.v1';
export const mitigationTreatmentRecordsCapability = 'mitigation.treatment-records.v1';
export const mitigationMoldClearanceCapability = 'mitigation.mold-clearance.v1';
export const mitigationFieldEvidenceReferenceSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('platform-storage-object'),
        contractVersion: z.literal('platform.storage-object-reference.v1'),
        objectId: mitigationDryingIdSchema,
        label: z.string().trim().min(1).max(240).nullable().default(null),
    }),
    z.strictObject({
        kind: z.literal('mitigation-moisture-evidence'),
        contractVersion: z.literal('mitigation.moisture-evidence.v1'),
        evidenceId: mitigationDryingIdSchema,
        label: z.string().trim().min(1).max(240).nullable().default(null),
    }),
]);
export const mitigationDailyMonitoringAssetPlacementReferenceSchema = z.strictObject({
    contractVersion: z.literal('assets.placements.v1'),
    placementId: mitigationDryingIdSchema,
    assetId: mitigationDryingIdSchema,
    assetRevision: z.number().int().positive(),
    label: z.string().trim().min(1).max(500),
    assetKind: z.literal('dehumidifier'),
    roomLocation: z.string().trim().min(1).max(240).nullable().default(null),
    placedAt: z.string().datetime({ offset: true }),
});
export const mitigationDailyMonitoringPointReferenceSchema = z.strictObject({
    pointId: mitigationDryingIdSchema,
    pointRevision: z.number().int().positive(),
    roomId: mitigationDryingIdSchema,
    roomLabel: z.string().trim().min(1).max(160),
    pointLabel: z.string().trim().min(1).max(160),
    materialType: z.enum(['drywall', 'hardwood', 'concrete', 'carpet', 'plywood', 'other']),
    dryGoalMillis: z.number().int().min(0).max(999_000).nullable(),
});
const evidenceRequirement = {
    requiresEvidence: z.boolean(),
    minimumEvidenceCount: z.number().int().min(0).max(20),
};
export const mitigationDailyMonitoringItemDefinitionSchema = z.discriminatedUnion('itemType', [
    z.strictObject({
        itemType: z.literal('moisture-point'),
        label: z.string().trim().min(1).max(500),
        point: mitigationDailyMonitoringPointReferenceSchema,
        ...evidenceRequirement,
    }),
    z.strictObject({
        itemType: z.literal('equipment-check'),
        label: z.string().trim().min(1).max(500),
        placement: mitigationDailyMonitoringAssetPlacementReferenceSchema,
        ...evidenceRequirement,
    }),
    z.strictObject({
        itemType: z.literal('air-reading'),
        label: z.string().trim().min(1).max(500),
        area: z.enum(['affected', 'unaffected', 'exterior']),
        verifiedWeatherAllowed: z.boolean(),
        ...evidenceRequirement,
    }),
]);
export const mitigationDailyMonitoringMeasurementSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        kind: z.literal('moisture'),
        valueMillis: z.number().int().min(0).max(2_000_000),
        unit: z.enum(['percent', 'relative']),
    }),
    z.strictObject({
        kind: z.literal('thermo'),
        temperatureFahrenheitMillis: z.number().int().min(-100_000).max(250_000),
        relativeHumidityMillis: z.number().int().min(0).max(100_000),
        grainsPerPoundMillis: z.number().int().min(0).max(1_000_000),
    }),
    z.strictObject({
        kind: z.literal('verified-weather'),
        temperatureFahrenheitMillis: z.number().int().min(-100_000).max(250_000),
        relativeHumidityMillis: z.number().int().min(0).max(100_000),
        grainsPerPoundMillis: z.number().int().min(0).max(1_000_000),
        providerReference: z.string().trim().min(1).max(500),
        observedAt: z.string().datetime({ offset: true }),
    }),
]);
export const mitigationDailyMonitoringItemSchema = z.strictObject({
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    sessionId: mitigationDryingIdSchema,
    itemKey: z.string().trim().min(1).max(500),
    definition: mitigationDailyMonitoringItemDefinitionSchema,
    checkedAt: z.string().datetime({ offset: true }).nullable(),
    checkedByMembershipId: mitigationDryingIdSchema.nullable(),
    measurement: mitigationDailyMonitoringMeasurementSchema.nullable(),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema).max(20),
    notes: z.string().trim().min(1).max(2_000).nullable(),
    revision: z.number().int().positive(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((item, context) => {
    if ((item.checkedAt === null) !== (item.checkedByMembershipId === null)) {
        context.addIssue({ code: 'custom', path: ['checkedAt'], message: 'Checked identity is incomplete.' });
    }
    if (item.checkedAt === null && item.measurement !== null) {
        context.addIssue({
            code: 'custom',
            path: ['measurement'],
            message: 'Unchecked monitoring items cannot retain measurements.',
        });
    }
});
export const mitigationDailyMonitoringMissingReasonSchema = z.enum([
    'not-checked',
    'missing-measurement',
    'missing-evidence',
]);
export const mitigationDailyMonitoringMissingItemSchema = z.strictObject({
    itemId: mitigationDryingIdSchema,
    itemKey: z.string().trim().min(1).max(500),
    itemType: z.enum(['moisture-point', 'equipment-check', 'air-reading']),
    label: z.string().trim().min(1).max(500),
    reasons: z.array(mitigationDailyMonitoringMissingReasonSchema).min(1),
});
export const mitigationDailyMonitoringSessionStatusSchema = z.enum([
    'open',
    'submitted',
    'override-submitted',
]);
export const mitigationDailyMonitoringSessionSchema = z.strictObject({
    contractVersion: z.literal(mitigationDailyMonitoringCapability),
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    workDate: mitigationDryingDateSchema,
    dayNumber: z.number().int().positive(),
    status: mitigationDailyMonitoringSessionStatusSchema,
    revision: z.number().int().positive(),
    startedByMembershipId: mitigationDryingIdSchema,
    startedAt: z.string().datetime({ offset: true }),
    submittedByMembershipId: mitigationDryingIdSchema.nullable(),
    submittedAt: z.string().datetime({ offset: true }).nullable(),
    overrideReason: z.string().trim().min(10).max(1_000).nullable(),
    missingAtSubmission: z.array(mitigationDailyMonitoringMissingItemSchema),
    submissionHash: z.string().regex(/^[a-f0-9]{64}$/).nullable(),
    items: z.array(mitigationDailyMonitoringItemSchema),
    missing: z.array(mitigationDailyMonitoringMissingItemSchema),
    readyToSubmit: z.boolean(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
}).superRefine((session, context) => {
    const submitted = session.status !== 'open';
    if (submitted !== (session.submittedAt !== null && session.submittedByMembershipId !== null)) {
        context.addIssue({ code: 'custom', path: ['submittedAt'], message: 'Submission identity is incomplete.' });
    }
    if (submitted !== (session.submissionHash !== null)) {
        context.addIssue({ code: 'custom', path: ['submissionHash'], message: 'Submitted evidence requires a snapshot hash.' });
    }
    if ((session.status === 'override-submitted') !== (session.overrideReason !== null)) {
        context.addIssue({ code: 'custom', path: ['overrideReason'], message: 'Only override submissions carry an override reason.' });
    }
});
export const mitigationStartDailyMonitoringCommandSchema = z.strictObject({
    kind: z.literal('session.start'),
    commandId: mitigationDryingIdSchema,
    sessionId: mitigationDryingIdSchema,
    workDate: mitigationDryingDateSchema,
    assetPlacements: z.array(mitigationDailyMonitoringAssetPlacementReferenceSchema).max(500).default([]),
    reason: mitigationDryingReasonSchema,
});
export const mitigationUpdateDailyMonitoringItemCommandSchema = z.strictObject({
    kind: z.literal('item.update'),
    commandId: mitigationDryingIdSchema,
    sessionId: mitigationDryingIdSchema,
    itemId: mitigationDryingIdSchema,
    expectedRevision: z.number().int().positive(),
    checked: z.boolean(),
    measurement: mitigationDailyMonitoringMeasurementSchema.nullable(),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema).max(20),
    notes: z.string().trim().min(1).max(2_000).nullable(),
    reason: mitigationDryingReasonSchema,
});
export const mitigationSubmitDailyMonitoringCommandSchema = z.strictObject({
    kind: z.literal('session.submit'),
    commandId: mitigationDryingIdSchema,
    sessionId: mitigationDryingIdSchema,
    expectedRevision: z.number().int().positive(),
    overrideReason: z.string().trim().min(10).max(1_000).nullable().default(null),
    reason: mitigationDryingReasonSchema,
});
export const mitigationDailyMonitoringCommandSchema = z.discriminatedUnion('kind', [
    mitigationStartDailyMonitoringCommandSchema,
    mitigationUpdateDailyMonitoringItemCommandSchema,
    mitigationSubmitDailyMonitoringCommandSchema,
]);
export const mitigationDailyMonitoringResponseSchema = z.strictObject({
    data: mitigationDailyMonitoringSessionSchema,
    command: z.strictObject({
        commandId: mitigationDryingIdSchema,
        idempotent: z.boolean(),
    }).optional(),
});
export const mitigationTreatmentKindSchema = z.enum(['antimicrobial', 'containment']);
const treatmentCommandBase = {
    kind: z.literal('record'),
    commandId: mitigationDryingIdSchema,
    recordId: mitigationDryingIdSchema,
    occurredAt: z.string().datetime({ offset: true }),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema).max(20).default([]),
    notes: z.string().trim().min(1).max(5_000).nullable().default(null),
    correctsRecordId: mitigationDryingIdSchema.nullable().default(null),
    reason: mitigationDryingReasonSchema,
};
export const mitigationRecordTreatmentCommandSchema = z.discriminatedUnion('treatmentKind', [
    z.strictObject({
        ...treatmentCommandBase,
        treatmentKind: z.literal('antimicrobial'),
        product: z.string().trim().min(1).max(240),
        location: z.string().trim().min(1).max(500),
        clientAcknowledged: z.boolean(),
        acknowledgementEvidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema).max(20).default([]),
    }),
    z.strictObject({
        ...treatmentCommandBase,
        treatmentKind: z.literal('containment'),
        location: z.string().trim().min(1).max(500),
        pressureValueMillis: z.number().int().min(-10_000_000).max(10_000_000),
        pressureUnit: z.enum(['pascal', 'inch-water-column']),
    }),
]);
export const mitigationVoidTreatmentCommandSchema = z.strictObject({
    kind: z.literal('void'),
    commandId: mitigationDryingIdSchema,
    recordId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
});
const voidEvidenceSchema = z.strictObject({
    commandId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
    actorMembershipId: mitigationDryingIdSchema,
    voidedAt: z.string().datetime({ offset: true }),
});
export const mitigationTreatmentRecordSchema = z.strictObject({
    contractVersion: z.literal(mitigationTreatmentRecordsCapability),
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    treatmentKind: mitigationTreatmentKindSchema,
    product: z.string().trim().min(1).max(240).nullable(),
    location: z.string().trim().min(1).max(500),
    pressureValueMillis: z.number().int().min(-10_000_000).max(10_000_000).nullable(),
    pressureUnit: z.enum(['pascal', 'inch-water-column']).nullable(),
    clientAcknowledged: z.boolean().nullable(),
    acknowledgementEvidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema),
    occurredAt: z.string().datetime({ offset: true }),
    receivedAt: z.string().datetime({ offset: true }),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema),
    notes: z.string().trim().min(1).max(5_000).nullable(),
    correctsRecordId: mitigationDryingIdSchema.nullable(),
    recordedByMembershipId: mitigationDryingIdSchema,
    void: voidEvidenceSchema.nullable(),
}).superRefine((record, context) => {
    const antimicrobial = record.treatmentKind === 'antimicrobial';
    const antimicrobialFields = record.product !== null
        && record.clientAcknowledged !== null
        && record.pressureValueMillis === null
        && record.pressureUnit === null;
    if (antimicrobial !== antimicrobialFields) {
        context.addIssue({ code: 'custom', path: ['product'], message: 'Treatment fields do not match the record kind.' });
    }
    const containmentFields = record.product === null
        && record.clientAcknowledged === null
        && record.acknowledgementEvidenceReferences.length === 0
        && record.pressureValueMillis !== null
        && record.pressureUnit !== null;
    if (antimicrobial === containmentFields) {
        context.addIssue({ code: 'custom', path: ['pressureValueMillis'], message: 'Pressure fields do not match the record kind.' });
    }
});
export const mitigationTreatmentListQuerySchema = z.strictObject({
    includeVoided: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const pageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const mitigationTreatmentResponseSchema = z.strictObject({
    data: mitigationTreatmentRecordSchema,
    command: z.strictObject({ commandId: mitigationDryingIdSchema, idempotent: z.boolean() }).optional(),
});
export const mitigationTreatmentsResponseSchema = z.strictObject({
    data: z.array(mitigationTreatmentRecordSchema),
    page: pageSchema,
});
export const mitigationClearanceStatusSchema = z.enum(['pending', 'passed', 'failed']);
export const mitigationClearanceMethodSchema = z.enum([
    'visual',
    'air-sample',
    'surface-sample',
    'clearance-letter',
]);
export const mitigationRecordMoldClearanceCommandSchema = z.strictObject({
    kind: z.literal('record'),
    commandId: mitigationDryingIdSchema,
    recordId: mitigationDryingIdSchema,
    status: mitigationClearanceStatusSchema,
    assessorName: z.string().trim().min(1).max(200).nullable(),
    assessorCompany: z.string().trim().min(1).max(200).nullable(),
    method: mitigationClearanceMethodSchema,
    assessmentDate: mitigationDryingDateSchema,
    conditionAchieved: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema).max(20).default([]),
    notes: z.string().trim().min(1).max(5_000).nullable().default(null),
    correctsRecordId: mitigationDryingIdSchema.nullable().default(null),
    reason: mitigationDryingReasonSchema,
}).superRefine((record, context) => {
    if (record.assessorName === null && record.assessorCompany === null) {
        context.addIssue({ code: 'custom', path: ['assessorName'], message: 'An assessor name or company is required.' });
    }
    if (record.status === 'passed' && record.conditionAchieved !== 1) {
        context.addIssue({ code: 'custom', path: ['conditionAchieved'], message: 'A passed clearance must document Condition 1.' });
    }
});
export const mitigationVoidMoldClearanceCommandSchema = z.strictObject({
    kind: z.literal('void'),
    commandId: mitigationDryingIdSchema,
    recordId: mitigationDryingIdSchema,
    reason: mitigationDryingReasonSchema,
});
export const mitigationMoldClearanceRecordSchema = z.strictObject({
    contractVersion: z.literal(mitigationMoldClearanceCapability),
    tenantId: mitigationDryingIdSchema,
    id: mitigationDryingIdSchema,
    projectId: mitigationDryingIdSchema,
    status: mitigationClearanceStatusSchema,
    assessorName: z.string().trim().min(1).max(200).nullable(),
    assessorCompany: z.string().trim().min(1).max(200).nullable(),
    method: mitigationClearanceMethodSchema,
    assessmentDate: mitigationDryingDateSchema,
    conditionAchieved: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    evidenceReferences: z.array(mitigationFieldEvidenceReferenceSchema),
    notes: z.string().trim().min(1).max(5_000).nullable(),
    correctsRecordId: mitigationDryingIdSchema.nullable(),
    recordedByMembershipId: mitigationDryingIdSchema,
    receivedAt: z.string().datetime({ offset: true }),
    void: voidEvidenceSchema.nullable(),
}).superRefine((record, context) => {
    if (record.assessorName === null && record.assessorCompany === null) {
        context.addIssue({ code: 'custom', path: ['assessorName'], message: 'An assessor name or company is required.' });
    }
    if (record.status === 'passed' && record.conditionAchieved !== 1) {
        context.addIssue({ code: 'custom', path: ['conditionAchieved'], message: 'A passed clearance must document Condition 1.' });
    }
});
export const mitigationMoldClearanceListQuerySchema = mitigationTreatmentListQuerySchema;
export const mitigationMoldClearanceResponseSchema = z.strictObject({
    data: mitigationMoldClearanceRecordSchema,
    command: z.strictObject({ commandId: mitigationDryingIdSchema, idempotent: z.boolean() }).optional(),
});
export const mitigationMoldClearancesResponseSchema = z.strictObject({
    data: z.array(mitigationMoldClearanceRecordSchema),
    page: pageSchema,
});
//# sourceMappingURL=mitigation-field-completion.contract.js.map